import { memo, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { Box, Grid, IconButton, Typography, useTheme } from "@mui/material";

import RouterLink from "../../../../common/components/RouterLink";
import { useGetNonAdminAccess } from "../../../../common/hooks/useGetNonAdminAccess";
import { RouteEnum } from "../../../../common/models/enums";
import {
  selectedStreamObjectVariable,
  useSelectedStreamObject,
} from "../../../../common/variables/selectedStreamObject";
import {
  selectedLocationDropdownVariable,
  useSelectedLocationDropdown,
} from "../../../../common/variables/selectedLocationDropdown";
import { useGetLocationsCached } from "../../../customer-settings/tabs/locations/hooks/useGetLocationsCached";
import { usePublishNode } from "../../../devices/hooks/usePublishNode";
import DevicesImageList from "./components/devices/DevicesImageList";

const DataHubLiveViewPageContent = () => {
  const element = document.getElementById("back-button");
  const theme = useTheme();

  const [searchParams, setSearchParams] = useSearchParams();
  const locationParam = searchParams.get("location");
  const deviceParam = searchParams.get("device");

  const { locations } = useGetLocationsCached("cache-only");
  const selectedLocation = useSelectedLocationDropdown();

  // Track whether initial URL→dropdown sync is complete
  const [initialSyncDone, setInitialSyncDone] = useState(false);

  // Sync URL → dropdown (only on initial page load from shared link)
  useEffect(() => {
    if (initialSyncDone) return;

    if (locations?.length) {
      setInitialSyncDone(true);

      if (locationParam) {
        const matchingLocation = locations.find(
          loc => loc.name.toLowerCase() === locationParam.toLowerCase()
        );

        if (matchingLocation) {
          selectedLocationDropdownVariable({
            title: matchingLocation.name,
            value: matchingLocation.id,
          });
        }
      }
    }
  }, [locationParam, locations, initialSyncDone]);

  // Sync dropdown → URL (after initial sync, keep URL in sync with dropdown)
  useEffect(() => {
    if (!initialSyncDone) return;

    setSearchParams(
      prev => {
        const newParams = new URLSearchParams(prev);

        if (selectedLocation?.title) {
          newParams.set("location", selectedLocation.title);
        } else {
          newParams.delete("location");
        }

        // Only clear device when location actually changed
        const currentLocationInUrl = prev.get("location");

        if (
          currentLocationInUrl &&
          currentLocationInUrl !== selectedLocation?.title
        ) {
          newParams.delete("device");
        }

        return newParams;
      },
      { replace: true }
    );
  }, [selectedLocation?.value, initialSyncDone]);

  const { nonAdminView } = useGetNonAdminAccess();

  const { publishNode } = usePublishNode();

  const selectedStreamObject = useSelectedStreamObject();

  useEffect((): void => {
    if (selectedStreamObject) {
      const { streamName, extractedDeviceName, nodeId } = selectedStreamObject;

      publishNode({
        message: JSON.stringify({
          TARGET: "KINESIS_STREAM",
          ACTION: "KINESIS_STREAM_END",
          streamName,
          deviceName: extractedDeviceName,
        }),
        nodeId,
      });

      selectedStreamObjectVariable(null);
    }
  }, []);

  return (
    <>
      {element &&
        createPortal(
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "1em",
            }}
          >
            <RouterLink
              to={
                nonAdminView
                  ? RouteEnum.SensoryRecentActivities
                  : RouteEnum.RecentActivities
              }
            >
              <IconButton
                sx={{
                  color: theme.palette.text.primary,
                  cursor: "pointer",
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
            </RouterLink>
            <Typography variant="h5">Recent activities</Typography>
          </Box>,
          element as HTMLElement
        )}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <DevicesImageList targetDeviceName={deviceParam} />
        </Grid>
      </Grid>
    </>
  );
};

export default memo(DataHubLiveViewPageContent);
